import { useRef, useState } from "react";
import { Camera, Upload, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { fileToCompressedDataUrl } from "../lib/image";

export default function CameraCapture({ value, onChange, testid = "photo" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [streaming, setStreaming] = useState(false);

  const start = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = s;
      setStreaming(true);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play(); } }, 50);
    } catch {
      toast.error("Camera not available. Please upload a photo instead.");
    }
  };
  const stop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; setStreaming(false); };
  const capture = () => {
    const v = videoRef.current;
    const maxW = 640;
    const vw = v.videoWidth || 480;
    const vh = v.videoHeight || 640;
    const scale = Math.min(1, maxW / vw);
    const c = document.createElement("canvas");
    c.width = Math.round(vw * scale); c.height = Math.round(vh * scale);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    onChange(c.toDataURL("image/jpeg", 0.7));
    stop();
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) { return; }
    try {
      onChange(await fileToCompressedDataUrl(f, { maxW: 640, quality: 0.7 }));
    } catch {
      toast.error("Could not read image. Please try another file.");
    }
    e.target.value = "";
  };

  return (
    <div data-testid={`${testid}-capture`}>
      {value ? (
        <div className="flex items-center gap-4">
          <img src={value} alt="capture" className="w-28 h-32 object-cover rounded-sm border border-line" />
          <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1.5 text-xs text-destructive hover:underline"><X size={14} /> Remove / Retake</button>
        </div>
      ) : streaming ? (
        <div>
          <video ref={videoRef} className="w-48 rounded-sm border border-line bg-black" muted playsInline />
          <div className="flex gap-2 mt-2">
            <button type="button" data-testid={`${testid}-snap`} onClick={capture} className="inline-flex items-center gap-1.5 bg-forest text-bone px-4 py-2 rounded-full text-xs uppercase tracking-widest"><Camera size={14} /> Capture</button>
            <button type="button" onClick={stop} className="inline-flex items-center gap-1.5 border border-line px-4 py-2 rounded-full text-xs uppercase tracking-widest"><RefreshCw size={14} /> Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid={`${testid}-start-camera`} onClick={start} className="inline-flex items-center gap-1.5 bg-forest text-bone px-4 py-2 rounded-full text-xs uppercase tracking-widest"><Camera size={14} /> Use Camera</button>
          <label className="inline-flex items-center gap-1.5 border border-forest text-forest px-4 py-2 rounded-full text-xs uppercase tracking-widest cursor-pointer">
            <Upload size={14} /> Upload
            <input data-testid={`${testid}-upload`} type="file" accept="image/*" onChange={onFile} className="hidden" />
          </label>
        </div>
      )}
    </div>
  );
}
